/* eslint-disable react/react-in-jsx-scope */
import {
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    createStyles,
    makeStyles,
    FormHelperText,
} from '@material-ui/core';
import {ComponentProps, useLayoutEffect, useRef, useState} from 'react';
import Utils from '../../../Core/Utils';
import {isOptionDisabled} from '../../Disabled/DisabledOptions';
import {FormType, ValueChanged} from '../types';
import {Parameter} from '../../../Core/mobx/ParameterStore/Parameter';
import {labelTextStyle} from '@/Core/UI';

const useStyles = makeStyles(() =>
    createStyles({
        formControl: {
            marginTop: 8,
        },
    }),
);

function validateMultiple(values: any[], options: any[]) {
    const normalizedOptions = options.map((o) => Utils.MakeVarName(o));
    const normalizedValues = values ? new Array<any>().concat(values) : [];
    return normalizedValues.filter((item) => normalizedOptions.indexOf(item) < 0);
}

function validateSingle(value: any, options: any[]) {
    return options.findIndex((op) =>
        typeof op === 'string' ? String(Utils.MakeVarName(op)) === String(value) : op === value,
    ) === -1 &&
        value !== null &&
        value !== undefined &&
        value !== ''
        ? [value]
        : '';
}

type ExtendedParameter = Parameter & {
    label: string;
    name: string;
    options: Array<string | number>;
    optionValues?: Array<string>;
    category?: string;
};

export type SelectInputElementProps = {
    param: ExtendedParameter;
    required: string | undefined | JSX.Element;
    formType?: FormType;
    valueChanged: ValueChanged;
    form?: boolean;
    valueChangedForm?: any;
    helperText?: string;
    isOptionDisabledCb?: (option: any) => boolean;
    optionDisplayFormatter?: (option: string | number) => string | number;
    newLabelType?: boolean;
    showExtraLabel?: boolean;
    defaultValue?: any;
} & Omit<ComponentProps<typeof Select>, 'required'>;

export function SelectInputElement({
    param,
    required,
    valueChanged,
    formType,
    form,
    valueChangedForm,
    isOptionDisabledCb,
    optionDisplayFormatter,
    newLabelType,
    showExtraLabel,
    defaultValue,
    ...selectProps
}: SelectInputElementProps) {
    if (param.options == null) {
        return <div>no options defined for {param.label}</div>;
    }
    const classes = useStyles();
    let {value} = param;

    if (selectProps.multiple) {
        // eslint-disable-next-line no-param-reassign
        selectProps.renderValue = (selected: any) => {
            if (!selected.length) {
                return <em>None</em>;
            }
            return param.options.filter((o) => selected.includes(Utils.MakeVarName(o.toString()))).join(', ');
        };

        value = param.value ? [].concat(param.value) : [];
    }

    const additionalValues = selectProps.multiple
        ? validateMultiple(value, param.options)
        : validateSingle(
              value,
              param.optionValues && param.optionValues.length > 0 ? param.optionValues : param.options,
          );

    const labelRef = useRef<HTMLLabelElement>(null);
    const [isMultiline, setIsMultiline] = useState(false);

    useLayoutEffect(() => {
        if (labelRef.current) {
            const lineHeight = 28.8;
            setIsMultiline(labelRef.current.offsetHeight > lineHeight + 2);
        }
    }, [param.label, required]);

    return (
        <div style={{width: '100%'}}>
            <FormControl fullWidth variant="outlined" className={!newLabelType ? classes.formControl : undefined}>
                {newLabelType ? (
                    <label
                        id={`lbl${param.name}`}
                        htmlFor={param.name}
                        ref={labelRef}
                        style={{
                            ...labelTextStyle,
                            marginBottom: isMultiline ? 4 : 12,
                            display: 'inline-flex',
                            gap: 6,
                        }}
                    >
                        {required ? <span>{required}</span> : null}
                        <span>{param.label}</span>
                    </label>
                ) : (
                    <InputLabel id={`lbl${param.name}`} htmlFor="outlined-age-native-simple">
                        {param.label} {required}
                    </InputLabel>
                )}

                {showExtraLabel && value !== 0 && !value && (
                    <InputLabel
                        id={`lbl${param.name}`}
                        htmlFor="outlined-age-native-simple"
                        shrink={false}
                        style={{
                            display: 'inline-flex',
                            top: 65,
                            position: 'absolute',
                            pointerEvents: 'none',
                        }}
                    >
                        {param.label} {required}
                    </InputLabel>
                )}

                <Select
                    labelId={`lbl${param.name}`}
                    aria-labelledby={`lbl${param.name}`}
                    id={param.name}
                    value={defaultValue ? defaultValue : value}
                    {...(!newLabelType && {label: required ? `${param.label} *` : param.label})}
                    onChange={(e) => {
                        if (form) {
                            valueChangedForm(e.target.value);
                        }
                        valueChanged(param, e.target.value, formType);
                    }}
                    style={newLabelType ? {height: 48} : undefined}
                    {...selectProps}
                >
                    {param.options.map((option) => (
                        <MenuItem
                            key={option}
                            value={
                                typeof option === 'string' && !Utils.IsNumeric(option)
                                    ? Utils.MakeVarName(option)
                                    : option
                            }
                            disabled={isOptionDisabled(option) || isOptionDisabledCb?.(option)}
                        >
                            {selectProps.multiple ? (
                                <>
                                    <Checkbox checked={value.indexOf(Utils.MakeVarName(option.toString())) > -1} />
                                    <ListItemText primary={option} />
                                </>
                            ) : (
                                optionDisplayFormatter?.(option) || option
                            )}
                        </MenuItem>
                    ))}
                </Select>
                {!!selectProps.helperText && <FormHelperText error>{selectProps.helperText}</FormHelperText>}
                {additionalValues && additionalValues.length ? (
                    <span style={{fontSize: 11}}>*Missing value: {String(additionalValues)}</span>
                ) : (
                    ''
                )}
            </FormControl>
        </div>
    );
}
